/**
 * Sprint 23.6 — AgentWorkforcePosition.
 *
 * The sidebar block that locates this hire inside the workforce hierarchy.
 * Two lines, each themed (Q17):
 *
 *   • Reports to — a single avatar + name → click navigates to the parent
 *     agent (Q14). Hidden when there's no agent ancestor (root agents only).
 *   • Manages    — N sub-agents via the SubAgentAvatarStack pattern. Hidden
 *     when N=0 (Q12).
 *
 * Pulls sub-agents from the existing `useListTree` query (shared with
 * AgentsPanel and the Workforce route) so the cache hit-rate stays high and
 * there's no per-profile fetch.
 */

import { useMemo, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { NodeAvatar } from "../primitives";
import { useListTree } from "../../../lib/hooks/use-kuvio";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { getAgentPersona } from "../cockpit/CockpitHeader";
import { SubAgentAvatarStack } from "./SubAgentAvatarStack";
import type { LibraryNodeDetail } from "../../../lib/types";

export interface AgentWorkforcePositionProps {
  node: LibraryNodeDetail;
}

export function AgentWorkforcePosition({ node }: AgentWorkforcePositionProps) {
  const navigate = useNavigate();
  const { currentWorkspaceId } = useAuth();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const labels = useThemedLabels();
  const isRoot = !!node.is_root;

  // Reports-to = nearest agent ancestor in the breadcrumb. Folders are skipped
  // because a hire reports to a hire, not a folder.
  const reportsTo = useMemo(() => {
    const trail = node._breadcrumb ?? [];
    for (let i = trail.length - 1; i >= 0; i--) {
      const seg = trail[i]!;
      if (seg.type === "agent") return seg;
    }
    return null;
  }, [node._breadcrumb]);

  const subAgents = useMemo(() => {
    return (tree.data ?? []).filter(
      (n) => n.parent_id === node.id && n.type === "agent" && !n.archived_at,
    );
  }, [tree.data, node.id]);

  // Nothing to show? Don't render the section at all.
  if (!reportsTo && subAgents.length === 0) return null;

  return (
    <aside style={wrap}>
      <h3 style={title}>{labels.workforcePositionTitle}</h3>

      {reportsTo && !isRoot && (
        <div style={row}>
          <div style={rowLabel}>{labels.reportsToLabel}</div>
          <button
            type="button"
            onClick={() => navigate(`/agents/${reportsTo.id}`)}
            style={parentBtn}
          >
            <NodeAvatar
              type="agent"
              persona={getAgentPersona({
                path: "",
                agent_config: null,
              })}
              size="sm"
              name={reportsTo.name}
            />
            <span style={parentName}>{reportsTo.name}</span>
          </button>
        </div>
      )}

      {subAgents.length > 0 && (
        <div style={row}>
          <div style={rowLabel}>{labels.managesLabel}</div>
          <SubAgentAvatarStack agents={subAgents} showAllLabel="Show all" />
        </div>
      )}
    </aside>
  );
}

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: "12px 14px",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "0.5px solid var(--color-border-secondary)",
};

const title: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)",
  margin: 0,
};

const row: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const rowLabel: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
};

const parentBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 4px",
  border: "none",
  background: "transparent",
  color: "var(--color-text-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  textAlign: "left",
};

const parentName: CSSProperties = {
  fontSize: 13,
};
